// lib/leasing/researchPass.ts
//
// Where a brand stands in a research run, read off the campaign row: the worker's research stage
// writes `metrics.research` (lib/leasing/researchStage.ts) and every open view follows it. The UI
// shows nothing for a brand not yet researched, pulses the ones being researched, and shows the
// rebuilt data once they are done (owner, 2026-09-26). No browser loop owns this any more.
import { ACTIVE_SOURCING_CAMPAIGN_STATUSES } from '#lib/leasing/sourcingCampaignLifecycle';

/** Every field a brand's research covers, in the order the card shows them. */
export const RESEARCH_FIELDS = ['website', 'logo', 'positioning', 'category', 'network', 'surface', 'legal', 'financials', 'expansion', 'stores', 'contacts'] as const;

export type CampaignResearch = { total: number; completed: number; current: string[]; done: string[]; credits: number; fields?: Record<string, string[]> };
export type ResearchState = 'waiting' | 'current' | 'done';

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

type CampaignLike = { status?: unknown; metrics?: unknown; config?: unknown } | null | undefined;

/** The research running on this campaign right now, or null when none is. */
export const activeCampaignResearch = (campaign: CampaignLike): CampaignResearch | null => {
  if (!campaign || !(ACTIVE_SOURCING_CAMPAIGN_STATUSES as readonly string[]).includes(String(campaign.status ?? ''))) return null;
  const config = campaign.config as { researchCompletedAt?: unknown } | null | undefined;
  if (config?.researchCompletedAt) return null;
  const raw = (campaign.metrics as { research?: Record<string, unknown> } | null | undefined)?.research;
  if (!raw || typeof raw !== 'object') return null;
  const research: CampaignResearch = {
    total: Number(raw.total) || 0,
    completed: Number(raw.completed) || 0,
    current: strings(raw.current),
    done: strings(raw.done),
    credits: Number(raw.credits) || 0,
    fields: raw.fields && typeof raw.fields === 'object'
      ? Object.fromEntries(Object.entries(raw.fields as Record<string, unknown>).map(([id, list]) => [id, strings(list)]))
      : undefined,
  };
  return research.completed < research.total || research.current.length > 0 ? research : null;
};

/** Where one brand stands in an active research run; null when no research is running. */
export const researchStateOf = (research: CampaignResearch | null | undefined, brandId: string | null | undefined): ResearchState | null => {
  if (!research || !brandId) return null;
  if (research.current.includes(brandId)) return 'current';
  return research.done.includes(brandId) ? 'done' : 'waiting';
};

/**
 * The fields of a brand being worked on right now: the ones a click or the agent named, or every
 * field while the worker's research has that brand in hand (it researches them all at once).
 */
export const researchingFieldsOf = (
  named: readonly string[] | undefined,
  research: CampaignResearch | null,
  brandId: string | null | undefined,
): readonly string[] | undefined =>
  named?.length ? named : brandId && research?.current.includes(brandId) ? (research.fields?.[brandId] ?? RESEARCH_FIELDS) : undefined;

const FIELD_LABELS: Record<string, [string, string]> = {
  website: ['website', 'site web'], logo: ['logo', 'logo'], positioning: ['positioning', 'positionnement'],
  category: ['category', 'catégorie'], network: ['network size', 'taille du réseau'], surface: ['store surface', 'surface des magasins'],
  legal: ['registered company', 'société au registre'], financials: ['revenue', 'chiffre d’affaires'],
  expansion: ['store openings', 'ouvertures'], stores: ['store list', 'liste des magasins'], contacts: ['contacts', 'contacts'],
};

/** The fields a row is being researched for, in the reader's words ("revenue, store openings"). */
export const researchFieldsLabel = (fields: readonly string[], lang: string): string =>
  fields.map((field) => FIELD_LABELS[field]?.[lang === 'fr' ? 1 : 0] ?? field).join(', ');

/** The pick a run made, or is making, for its brands: the campaign's config carries it. */
export type CampaignSelection = {
  /** Brands the run had to choose, over every asset. */
  total: number;
  /** Brands that passed the filters, over every asset. */
  eligible: number;
  choosing: boolean;
  method: 'model' | 'formula' | null;
  /** One reason per picked brand, keyed by brand id. */
  reasons: Record<string, string>;
  gaps: string | null;
  /** The set the pick chose from, as the chat's carousel shows it; empty once the run moved on. */
  candidates: Array<{ id: string; name: string; logoUrl: string | null }>;
  pickedIds: string[];
};

const sumValues = (value: unknown): number =>
  value && typeof value === 'object' ? Object.values(value as Record<string, unknown>).reduce<number>((sum, n) => sum + (Number(n) || 0), 0) : 0;

/** What the run chose, and whether it is still choosing; null when the run never had to choose. */
export const campaignSelection = (campaign: CampaignLike): CampaignSelection | null => {
  const config = campaign?.config as { selection?: Record<string, unknown> } | null | undefined;
  const raw = config?.selection;
  if (!raw || typeof raw !== 'object') return null;
  const reasons: Record<string, string> = {};
  const pickedIds: string[] = [];
  for (const pick of Array.isArray(raw.picked) ? raw.picked : []) {
    if (pick && typeof pick === 'object' && typeof (pick as { brandId?: unknown }).brandId === 'string') {
      pickedIds.push((pick as { brandId: string }).brandId);
      if (typeof (pick as { reason?: unknown }).reason === 'string') reasons[(pick as { brandId: string }).brandId] = (pick as { reason: string }).reason;
    }
  }
  const live = (campaign?.metrics as { selection?: { candidates?: unknown } } | null | undefined)?.selection?.candidates;
  const candidates = (Array.isArray(live) ? live : [])
    .filter((c): c is { id: string; name: string; logoUrl: string | null } => Boolean(c) && typeof (c as { id?: unknown }).id === 'string' && typeof (c as { name?: unknown }).name === 'string')
    .map((c) => ({ id: c.id, name: c.name, logoUrl: typeof c.logoUrl === 'string' ? c.logoUrl : null }));
  const active = (ACTIVE_SOURCING_CAMPAIGN_STATUSES as readonly string[]).includes(String(campaign?.status ?? ''));
  return {
    total: sumValues(raw.countByProperty),
    eligible: sumValues(raw.eligibleByProperty),
    choosing: active && !raw.selectedAt,
    method: raw.method === 'model' || raw.method === 'formula' ? raw.method : null,
    reasons,
    gaps: typeof raw.gaps === 'string' && raw.gaps.trim().length > 0 ? raw.gaps : null,
    candidates,
    pickedIds,
  };
};
